import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangleIcon, FilmIcon, Loader2Icon, MaximizeIcon, PauseIcon, PlayIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
interface VideoPlayerProps {
  /** Ruta del archivo MP4. Si está vacío, se muestra el placeholder listo para reemplazar. */
  src?: string;
  /** Imagen de portada que se ve antes de reproducir. */
  poster?: string;
  /** Título accesible del video. */
  title?: string;
  className?: string;
}
function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Reproductor de video MP4 con controles propios.
 * Uso: <VideoPlayer src="/videos/demo.mp4" poster="/imagenes/portada.jpg" title="Demo web" />
 */
export function VideoPlayer({
  src,
  poster,
  title = 'Demostración de página web YenniDigital',
  className = ''
}: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(src ? 'loading' : 'ready');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }, []);
  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);
  const handleSeek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const next = Number(event.target.value);
    video.currentTime = next;
    setCurrent(next);
  };
  const goFullscreen = () => {
    const node = containerRef.current;
    if (node && node.requestFullscreen) void node.requestFullscreen();
  };
  useEffect(() => {
    setStatus(src ? 'loading' : 'ready');
    setIsPlaying(false);
    setCurrent(0);
    setDuration(0);
  }, [src]);
  if (!src) {
    return <div className={`relative flex aspect-[16/10] w-full flex-col items-center justify-center gap-3 overflow-hidden rounded-lg border border-white/8 bg-ink-900 px-6 text-center ${className}`} data-id="element-258" data-mp="1jr5zi15xcy8l">
        <span className="grid h-12 w-12 place-items-center rounded-full border border-neon/40 bg-neon/10 text-neon" data-id="element-259" data-mp="s09n11ns0vlu">
          <FilmIcon className="h-5 w-5" aria-hidden="true" strokeWidth={1.5} data-id="element-260" data-mp="15wdamc45a5pc" />
        </span>
        <span className="text-sm font-medium text-mist" data-id="element-261" data-mp="10y22c5ti0398">Reproductor listo para tu video</span>
        <span className="text-[11px] leading-relaxed text-mist-dim" data-id="element-262" data-mp="2hbs8di46h1li">
          Añade tu archivo MP4 (1920×1200 · formato 16:10) y aparecerá aquí con controles
          personalizados.
        </span>
        <span aria-hidden="true" className="pointer-events-none absolute inset-x-8 top-0 h-px bg-neon/40" data-id="element-263" data-mp="3uau1lojd37wg" />
      </div>;
  }
  return <div ref={containerRef} className={`group relative aspect-[16/10] w-full overflow-hidden rounded-lg border border-white/8 bg-black ${className}`} data-id="element-264" data-mp="3lcig20xcr0av">
      <video ref={videoRef} src={src} poster={poster} title={title} autoPlay loop playsInline muted={isMuted} preload="metadata" onClick={togglePlay} onLoadedMetadata={event => {
      setDuration(event.currentTarget.duration);
      setStatus('ready');
    }} onTimeUpdate={event => setCurrent(event.currentTarget.currentTime)} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} onEnded={() => setIsPlaying(false)} onError={() => setStatus('error')} className="h-full w-full cursor-pointer object-cover" data-id="element-265" data-mp="hv087thkxtag" />

      {status === 'loading' ? <div className="absolute inset-0 grid place-items-center bg-ink-950/70" role="status" data-id="element-266" data-mp="384b3foq43llb">
          <Loader2Icon className="h-6 w-6 animate-spin text-neon" aria-hidden="true" data-id="element-267" data-mp="342pm9r6jol5b" />
          <span className="sr-only" data-id="element-268" data-mp="2z4ed7io2qeo5">Cargando video…</span>
        </div> : null}

      {status === 'error' ? <div role="alert" className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-ink-950/90 px-6 text-center" data-id="element-269" data-mp="3vuna6w9mfcab">
          <AlertTriangleIcon className="h-6 w-6 text-amber-300" aria-hidden="true" data-id="element-270" data-mp="3rv66o8dr8p7s" />
          <p className="text-sm font-medium text-white" data-id="element-271" data-mp="3mzz453w81qkg">No pudimos cargar el video</p>
          <p className="text-[11px] text-mist-dim" data-id="element-272" data-mp="3i2qmr5iwdqk9">Revisa la ruta del archivo MP4.</p>
        </div> : null}

      {status === 'ready' && !isPlaying ? <button type="button" onClick={togglePlay} aria-label="Reproducir video" className="absolute inset-0 grid place-items-center bg-ink-950/40 transition-colors duration-200 ease-out hover:bg-ink-950/25" data-id="element-273" data-mp="em0ai8wq2ha4">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-neon text-ink-950 shadow-glow transition-transform duration-200 ease-out hover:scale-105" data-id="element-274" data-mp="qychcld4m9kn">
            <PlayIcon className="ml-0.5 h-5 w-5" aria-hidden="true" data-id="element-275" data-mp="zyr5dshh62ru" />
          </span>
        </button> : null}

      {status !== 'error' ? <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 border-t border-white/10 bg-ink-950/85 px-3 py-2 backdrop-blur" data-id="element-276" data-mp="19v3wqjpjs4jf">
          <button type="button" onClick={togglePlay} aria-label={isPlaying ? 'Pausar video' : 'Reproducir video'} className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-neon/40 text-neon transition-colors duration-150 ease-out hover:bg-neon/15" data-id="element-277" data-mp="1enevvkcgrmax">
            {isPlaying ? <PauseIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-278" data-mp="3ooqb1nat6m6w" /> : <PlayIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-279" data-mp="3knem7jzre4zr" />}
          </button>

          <input type="range" min={0} max={duration || 0} step={0.1} value={current} onChange={handleSeek} aria-label="Progreso del video" className="h-1 w-full cursor-pointer appearance-none rounded-full bg-white/20 accent-neon" data-id="element-280" data-mp="29f30lbsrjcs" />

          <span className="shrink-0 text-[10px] tabular-nums text-mist-muted" data-id="element-281" data-mp="77gnnaovfqqd">
            {formatTime(current)} / {formatTime(duration)}
          </span>

          <button type="button" onClick={toggleMute} aria-label={isMuted ? 'Activar sonido' : 'Silenciar video'} className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-mist-muted transition-colors duration-150 ease-out hover:text-neon" data-id="element-282" data-mp="g96mh1l31ry6">
            {isMuted ? <VolumeXIcon className="h-4 w-4" aria-hidden="true" data-id="element-283" data-mp="xjk1h3xdjz5e" /> : <Volume2Icon className="h-4 w-4" aria-hidden="true" data-id="element-284" data-mp="12hva5bbxo3m1" />}
          </button>

          <button type="button" onClick={goFullscreen} aria-label="Ver en pantalla completa" className="hidden h-8 w-8 shrink-0 place-items-center rounded-full text-mist-muted transition-colors duration-150 ease-out hover:text-neon sm:grid" data-id="element-285" data-mp="or0a8ojewwul">
            <MaximizeIcon className="h-4 w-4" aria-hidden="true" data-id="element-286" data-mp="jrdv29k0x6j5" />
          </button>
        </div> : null}
    </div>;
}